import { ErrorHandler, Injectable } from "@angular/core";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { MessageTrayService } from "./message-tray/message-tray.service";
import { Level, MessageTrayData } from "./message-tray/message-tray.data";

export interface ErrorContext {
  context?: string;
  component?: string;
  crid?: string;
  url?: string;
  startDate?: Date;
  endDate?: Date;
  decodedValue?: any;
  showAlert? : boolean;
  allowDuplicate?: boolean
}

@Injectable({
  providedIn: "root",
})

export class AngularAppLogger implements ErrorHandler {
  readonly preFix = "Uncaught (in promise): ";
  messageTrayData: MessageTrayData = new MessageTrayData();

  constructor(readonly mTrayServ: MessageTrayService) { }

  logInfo(message: any, context?: ErrorContext): void {
    const formattedMessage = this.appendContextInfo(this.formatMessage(message), context);
    this.setMessageTray(formattedMessage, Level.INFO, context?.allowDuplicate);
  }

  logError(error: any, context?: ErrorContext): void {
    if (this.shouldSuppressedError(error)) return;

    const formatted = this.appendContextInfo(this.formatMessage(error), context);
    this.setMessageTray(formatted, Level.ERROR, context?.allowDuplicate);

    if (context?.showAlert) {
      this.showGlobalAlert(formatted, context);
    }
  }

  /**
   * Attach the Context info to the log and format a proper json object
   * @param message
   * @param context
   */
  appendContextInfo(message: any, context?: ErrorContext): any {
    const logObject: any = (typeof message === 'object' && message !== null) ? { ...message } : { message: String(message) };
    if (!context) {
      return logObject;
    }

    const loggerContext: any = {};
    if (context.component) loggerContext.component = context.component;
    if (context.context) loggerContext.context = context.context;
    if (context.url) loggerContext.url = context.url;
    if (context.crid) loggerContext.crid = context.crid;
    if (context.startDate) loggerContext.startDate = context.startDate.toISOString();
    if (context.endDate) loggerContext.endDate = context.endDate.toISOString();
    if (context.decodedValue) loggerContext.decodedValue = context.decodedValue;

    if (Object.keys(loggerContext).length > 0) {
      logObject.AppLoggerContext = loggerContext;
    }
    return logObject;
  }

  formatMessage(msg: any): any {
    if (!msg) return { message: "Unknown error." };

    // Check if msg contains prefix
    if (typeof msg === "object" && msg.message?.startsWith(this.preFix)) {
      msg.message = msg.message.substring(this.preFix.length);
    }

    // Check if msg is a string
    if (typeof msg === "string") {
      return {message: msg};
    }

    // Check if its an HttpErrorResponse
    if (msg instanceof HttpErrorResponse) {
      if (typeof msg.error === "string" && msg.error.toLowerCase().includes("support id")) {
        const supportId = msg.error.match(/\d+/g)?.[0];
        return { message: `There was an error processing your request.`, supportId };
      }
      return {
        message: `HTTP ${msg.status} - ${msg.message}`,
        url: msg.url || null,
        status: msg.status,
        body: msg.error || null
      };
    }

    // Check if its HttpResponse
    if (msg instanceof HttpResponse) {
      return {
        message: `HTTP ${msg.status}`,
        body: msg.body
      };
    }

    // check if its an Error type
    if (msg instanceof Error) {
      return {
        message: msg.message || "Unknown Error",
        stack: msg.stack || "No stack trace available"
      };
    }

    // Any other types
    return this.normalizeMessage(msg);
  }

  normalizeMessage(msg: any): any {
    // check if this is json
    if (this.isJson(msg)) {
      return JSON.parse(msg);
    }

    // If there is error stack
    if (typeof msg === "object" && msg !== null) {
      const result: any = { ...msg };
      if (msg.stack && !result.stack) result.stack = msg.stack;
      if (msg.message && !result.message) result.message = msg.message;
      return result;
    }

    // else just make it a string object
    return { message: String(msg) };
  }


  /**
   * Extracts the center number from the decoded authz object (TC or RC)
   */
  extractCCN(appname: string, decoded: any): string | null {
    if (!decoded) return null;

    if (appname === "cra")
    {
      const clientArray = decoded.authz_cibmtr_fhir_ehr_client;
      for (const item of clientArray) {
        const match = item.match(/rc_(\d{5})/);
        if (match) return match[1];
      }
    }
    else {
      const clientArray = decoded.authz_msync_fhir_ehr_tc_role;
      for (const item of clientArray) {
        const match = item.match(/tc_(\d{3})/);
        if (match) return match[1];
      }
    }
    return null;
  }

  /**
   * Moving the oidc message suppression in a separate method
   * @param error
   */
  shouldSuppressedError(error: any): boolean {
    const message = error?.message || error?.toString() || "";
    // Suppress OIDC state errors since authentication is working
    if (message.includes("could not find matching config for state")) {
      console.log("OIDC state error suppressed - authentication is working correctly");
      return true;
    }
    return false;
  }

  /**
   * Implement the ErrorHandler.handleError to just call logError with a generic context message.
   * @param error
   */
  handleError(error: any) {
    const handleErrorContext: ErrorContext = {
      context: `Error was thrown by the App and caught by ErrorHandler`,
      allowDuplicate: true
    };
    this.logError(error, handleErrorContext);
  }

  // Offering the caller the option of updating the tcNum selected for MsPi
  setUserContext(appName: string, decodedValue: any, tcNum?: string) {
    if (!decodedValue) return;
    const userId = decodedValue.email || 'null';
    // If user changes tc selection in MsPi page
    if (tcNum) {
      this.setTcNum(userId, tcNum);
      return;
    }
    const ccn = tcNum? tcNum: this.extractCCN(appName, decodedValue);
    this.mTrayServ.setUserContext(userId, ccn);
    console.log(`[Logger] User context set: ${userId}, CCN: ${ccn}`);
    this.logInfo(decodedValue, { context: `Logging the decoded token from AppLogger` });
  }

  /**
   * Helper method to set tcNum and userID separately
   * @param userId
   * @param tcNum
   */
  setTcNum(userId: string, tcNum: string) {
    const centerNumber = tcNum.match(/\d+/)?.[0] || tcNum;
    this.mTrayServ.setUserContext(userId, centerNumber);
    this.logInfo(`[Logger] User context set: ${userId}, CCN: ${centerNumber}`,  { context: `TC selection changed`, allowDuplicate: true });
  }

  isJson(str: string) {
    try {
      JSON.parse(str);
    } catch (error) {
      return false;
    }
    return true;
  }

  setMessageTray(msg, lvl: Level, allowDuplicate?: boolean) {
    this.messageTrayData.logLevel = lvl;
    this.messageTrayData.message = msg;
    this.messageTrayData.timestamp = new Date();
    this.mTrayServ.setMessage(this.messageTrayData, allowDuplicate);
  }

  /**
   * Replacement for globalalert
   * @param message
   * @param context
   */
  showGlobalAlert(message: string, context?: ErrorContext) {
    const ctx: ErrorContext = context || {} as ErrorContext;
    let alertMsg = `${message}\n`;
    if (ctx?.url) {
      alertMsg += `URL: ${ctx.url}\n`;
    }
    if (ctx?.crid) {
      alertMsg += `CRID: ${ctx.crid}\n`;
    }
    if (ctx?.startDate && ctx?.endDate) {
      alertMsg += `Date Range: ${ctx.startDate} - ${ctx.endDate}\n`;
    }
    alertMsg += `Timestamp: ${new Date().toUTCString()}`;
    alert(alertMsg);
  }
}
